import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Logo } from "@/components/ui/Logo";
import { cn } from "@/lib/utils";

interface UcVendorLogoStripProps {
  /** Optional heading shown above the logo row. */
  heading?: string;
  /** Force light styling (for dark backgrounds). */
  variant?: "light" | "default";
  /** Logo size bucket. */
  size?: "sm" | "md" | "lg" | "xl";
  /** Wrap each logo in a white chip (recommended on coloured backgrounds). */
  chip?: boolean;
  className?: string;
}

/**
 * Horizontal strip of supported UC vendor logos, sourced from `uc_vendor_logos`.
 * Auto-updates whenever a new vendor logo is approved in the admin panel.
 * Vendors without a logo (image_id is null) are skipped.
 */
export function UcVendorLogoStrip({
  heading = "Works with the UC platforms you already use",
  variant = "default",
  size = "lg",
  chip = false,
  className,
}: UcVendorLogoStripProps) {
  const { data: vendors } = useQuery({
    queryKey: ["uc-vendor-logos-strip"],
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("uc_vendor_logos" as any)
        .select("slug, display_name, image_id, sort_order")
        .eq("active", true)
        .not("image_id", "is", null)
        .order("sort_order", { ascending: true })
        .order("display_name", { ascending: true });
      if (error) throw error;
      return (data as any[]) ?? [];
    },
  });

  if (!vendors || vendors.length === 0) return null;

  const headingClass =
    variant === "light" ? "text-primary-foreground/80" : "text-muted-foreground";

  // Every logo lives inside an identical bounding box so optical weight matches,
  // regardless of the source asset's aspect ratio. object-contain centers each one.
  return (
    <div className={cn("w-full", className)}>
      {heading && (
        <p
          className={cn(
            "text-center text-xs uppercase tracking-wider font-semibold mb-8",
            headingClass,
          )}
        >
          {heading}
        </p>
      )}
      <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-6 md:gap-x-12">
        {vendors.map((v) => {
          // Brand-pack assets are trimmed consistently, so every logo can share
          // one bounding box without per-vendor scaling hacks.
          const boxClass = "flex h-10 w-[140px] items-center justify-center";
          const imgClass = "!h-full !w-full !max-h-10 !max-w-[140px] object-contain";
          return (
            <div key={v.slug} className={boxClass}>
              <Logo
                vendorSlug={v.slug}
                size={size}
                chip={chip}
                alt={`${v.display_name} logo`}
                className="opacity-80 hover:opacity-100 transition-opacity duration-200"
                imgClassName={imgClass}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}
